/**
 * A rolling analytics session id for the first-party web pipeline. Distinct from the auth session
 * (see session-gate.ts): this only groups a visitor's events into a "visit" for funnel/sessionization
 * analysis. A new id starts after 30 minutes of inactivity; any event within that window extends it.
 */

const KEY = "blidz-web-session";
const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes
// Persist the activity timestamp at most this often. getSessionId runs on every event (incl. the
// capture-phase click handler), so writing localStorage each call adds needless main-thread I/O to
// the click path. Trade-off: the stored `last` can lag real activity by up to this much, so a session
// may roll over up to WRITE_THROTTLE_MS *before* a full 30 min of inactivity (≈29 min worst case).
// That skew is immaterial for sessionization, and worth avoiding a write on every click.
const WRITE_THROTTLE_MS = 60 * 1000; // 1 minute

interface StoredSession {
  id: string;
  last: number;
}

function newSessionId(): string {
  return `ws-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

/**
 * Return the current session id, rolling it over after 30 minutes of inactivity. Refreshes the stored
 * activity timestamp at most once per {@link WRITE_THROTTLE_MS}. Safe on the server (returns a
 * throwaway id when there is no localStorage).
 */
export function getSessionId(): string {
  if (typeof window === "undefined") {
    return newSessionId();
  }

  const now = Date.now();
  let session: StoredSession | null = null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw) {
      session = JSON.parse(raw) as StoredSession;
    }
  } catch {
    session = null;
  }

  // Treat a partial / corrupt record (missing string id, non-numeric last) as new, so getSessionId
  // never returns `undefined` for the id — that would violate the session_id: string contract and
  // break sessionization joins downstream.
  const isNew =
    !session ||
    typeof session.id !== "string" ||
    typeof session.last !== "number" ||
    now - session.last > INACTIVITY_MS;
  if (isNew) {
    session = { id: newSessionId(), last: now };
  }
  // session is non-null here (either loaded within the window, or just created).
  const current = session as StoredSession;

  // Write on a new session, or when the stored timestamp is stale enough to be worth refreshing —
  // not on every call.
  if (isNew || now - current.last > WRITE_THROTTLE_MS) {
    current.last = now;
    try {
      window.localStorage.setItem(KEY, JSON.stringify(current));
    } catch {
      // Private mode / storage full — the id is still returned for this call.
    }
  }
  return current.id;
}
